const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ParsedBulkEmails {
  /** Every normalised token in input order — what gets submitted. */
  all: string[];
  valid: string[];
  invalid: string[];
  duplicates: string[];
}

/**
 * Normalise a pasted blob of addresses (one per line, or comma/semicolon/space
 * separated, including Outlook-style `Name <user@example.com>`) into a clean
 * list. Mirrors the backend normaliser: trim, lowercase, strip surrounding
 * quotes/angle brackets. The full token list is submitted as-is so the
 * server's per-email result table accounts for every input —
 * valid/invalid/duplicates only feed the pre-submit count chip.
 */
export function parseBulkEmails(raw: string): ParsedBulkEmails {
  const all: string[] = [];
  const valid: string[] = [];
  const invalid: string[] = [];
  const duplicates: string[] = [];
  const seen = new Set<string>();

  const tokens: string[] = [];
  for (const segment of raw.split(/[\n,;]+/)) {
    // In a segment carrying `Name <addr>` pairs, take the bracketed addresses
    // and drop the bare name words next to them (anything without an @) rather
    // than reporting names as invalid emails.
    const hasBrackets = /<[^<>]*>/.test(segment);
    for (const match of segment.matchAll(/<([^<>]*)>|([^\s<>]+)/g)) {
      const token = match[1] ?? match[2] ?? '';
      if (match[2] !== undefined && hasBrackets && !token.includes('@')) continue;
      tokens.push(token);
    }
  }

  for (const token of tokens) {
    const email = token.replace(/^["'<]+/, '').replace(/[>"']+$/, '').toLowerCase();
    if (!email) continue;

    all.push(email);
    if (!EMAIL_RE.test(email)) {
      invalid.push(email);
    } else if (seen.has(email)) {
      duplicates.push(email);
    } else {
      seen.add(email);
      valid.push(email);
    }
  }

  return { all, valid, invalid, duplicates };
}
